import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const Homepage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isTranslateLoaded, setIsTranslateLoaded] = useState(false);
  const [marketData, setMarketData] = useState([
    { symbol: 'BTC', name: 'Bitcoin', price: '$43,250', change: '+2.3%', trend: 'up' },
    { symbol: 'ETH', name: 'Ethereum', price: '$2,890', change: '+1.8%', trend: 'up' },
    { symbol: 'SOL', name: 'Solana', price: '$102', change: '+5.2%', trend: 'up' }
  ]);
  const navigate = useNavigate();
  const location = useLocation();
  const tradingViewRef = useRef(null);
  const translateRef = useRef(null);

  useEffect(() => {
    // Initialize Google Translate with better error handling
    const initializeGoogleTranslate = () => {
      if (!window.google || !window.google.translate) {
        const script = document.createElement('script');
        script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
        script.async = true;
        
        script.onerror = (error) => {
          console.warn('Google Translate failed to load:', error);
          setIsTranslateLoaded(false);
        };
        
        document.head.appendChild(script);
      } else {
        initializeTranslateElement();
      }
    };

    const initializeTranslateElement = () => {
      try {
        if (translateRef.current && window.google.translate) {
          new window.google.translate.TranslateElement({
            pageLanguage: 'en',
            includedLanguages: 'en,es,fr,de,zh,ja,ko,ru,ar,hi',
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
            autoDisplay: false,
            gaTrack: true,
            gaId: 'UA-XXXXX-X'
          }, 'google_translate_element');
          setIsTranslateLoaded(true);
        }
      } catch (error) {
        console.error('Google Translate initialization error:', error);
        setIsTranslateLoaded(false);
      }
    };

    window.googleTranslateElementInit = initializeTranslateElement;
    initializeGoogleTranslate();

    // Intersection Observer for active section highlighting
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3 }
    );

    // Observe all sections
    document.querySelectorAll('section[id]').forEach((section) => {
      observer.observe(section);
    });

    // Load TradingView widget with better error handling
    const loadTradingView = () => {
      if (window.TradingView && tradingViewRef.current) {
        try {
          new window.TradingView.widget({
            width: "100%",
            height: "600",
            symbol: "BINANCE:BTCUSDT",
            interval: "D",
            timezone: "Etc/UTC",
            theme: "dark",
            style: "1",
            locale: "en",
            toolbar_bg: "#0f0f0f",
            enable_publishing: false,
            allow_symbol_change: true,
            container_id: "tradingview-widget",
            studies: [
              "RSI@tv-basicstudies",
              "EMA@tv-basicstudies"
            ],
            show_popup_button: true,
            popup_width: "1000",
            popup_height: "650",
          });
        } catch (error) {
          console.error('TradingView widget error:', error);
          // Fallback: Show static chart image or alternative
          tradingViewRef.current.innerHTML = `
            <div class="flex flex-col items-center justify-center h-full text-gray-500">
              <svg class="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <p>Chart temporarily unavailable</p>
              <p class="text-sm mt-2">Live charts will be available after registration</p>
            </div>
          `;
        }
      }
    };

    // Load TradingView script with better CDN
    if (!window.TradingView) {
      const tvScript = document.createElement('script');
      tvScript.src = 'https://s3.tradingview.com/tv.js';
      tvScript.async = true;
      tvScript.onload = () => {
        setTimeout(loadTradingView, 1000);
      };
      tvScript.onerror = () => {
        console.warn('TradingView script failed to load');
        if (tradingViewRef.current) {
          tradingViewRef.current.innerHTML = `
            <div class="flex flex-col items-center justify-center h-full text-gray-500">
              <svg class="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
              <p>Live charts loading failed</p>
              <button onclick="window.location.reload()" class="mt-4 px-4 py-2 bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors">
                Retry
              </button>
            </div>
          `;
        }
      };
      document.head.appendChild(tvScript);
    } else {
      loadTradingView();
    }

    // Simulate live market data updates
    const marketInterval = setInterval(() => {
      setMarketData(prev => prev.map(coin => ({
        ...coin,
        price: `$${(parseFloat(coin.price.replace(/[$,]/g, '')) + (Math.random() - 0.5) * 100).toFixed(0)}`,
        change: `${Math.random() > 0.5 ? '+' : ''}${(Math.random() * 3).toFixed(1)}%`,
        trend: Math.random() > 0.5 ? 'up' : 'down'
      })));
    }, 5000);

    return () => {
      if (window.googleTranslateElementInit) {
        window.googleTranslateElementInit = null;
      }
      observer.disconnect();
      clearInterval(marketInterval);
    };
  }, []);

  const handleNavigation = (path, event) => {
    if (event) {
      event.preventDefault();
    }

    if (path.startsWith('#')) {
      const element = document.querySelector(path);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      setIsMenuOpen(false);
    } else if (path === '/login' || path === '/register') {
      navigate(path);
    }
  };

  const features = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6">
          <path d="M14,9H10a1,1,0,0,0-1,1v4a1,1,0,0,0,1,1h4a1,1,0,0,0,1-1V10A1,1,0,0,0,14,9Zm-1,4H11V11h2Zm8,0a1,1,0,0,0,0-2H19V9h2a1,1,0,0,0,0-2H18.82A3,3,0,0,0,17,5.18V3a1,1,0,0,0-2,0V5H13V3a1,1,0,0,0-2,0V5H9V3A1,1,0,0,0,7,3V5.18A3,3,0,0,0,5.18,7H3A1,1,0,0,0,3,9H5v2H3a1,1,0,0,0,0,2H5v2H3a1,1,0,0,0,0,2H5.18A3,3,0,0,0,7,18.82V21a1,1,0,0,0,2,0V19h2v2a1,1,0,0,0,2,0V19h2v2a1,1,0,0,0,2,0V18.82A3,3,0,0,0,18.82,17H21a1,1,0,0,0,0-2H19V13Zm-4,3a1,1,0,0,1-1,1H8a1,1,0,0,1-1-1V8A1,1,0,0,1,8,7h8a1,1,0,0,1,1,1Z" fill="currentColor"/>
        </svg>
      ),
      title: "Modern Design",
      description: "Experience our cutting-edge interface designed for optimal trading performance and user experience across all devices."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6">
          <path d="M10.17,10.55a1,1,0,0,0,.73.45,1,1,0,0,0,.81-.29l1.13-1.14,1.33,2A1,1,0,0,0,15,12a.94.94,0,0,0,.55-.17,1,1,0,0,0,.28-1.38l-2-3A1,1,0,0,0,13.1,7a1,1,0,0,0-.81.29L11.16,8.43l-1.33-2a1,1,0,1,0-1.66,1.1ZM21,14H20V4h1a1,1,0,0,0,0-2H3A1,1,0,0,0,3,4H4V14H3a1,1,0,0,0,0,2h8v1.15l-4.55,3A1,1,0,0,0,7,22a.94.94,0,0,0,.55-.17L11,19.55V21a1,1,0,0,0,2,0V19.55l3.45,2.28A.94.94,0,0,0,17,22a1,1,0,0,0,.55-1.83l-4.55-3V16h8a1,1,0,0,0,0-2Zm-3,0H6V4H18Z" fill="currentColor"/>
        </svg>
      ),
      title: "Easy Customize",
      description: "Personalize your trading experience with customizable charts, layouts, and alerts tailored to your strategy."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6">
          <path d="M14,9H10a1,1,0,0,0-1,1V22a1,1,0,0,0,1,1h4a1,1,0,0,0,1-1V10A1,1,0,0,0,14,9ZM13,21H11V11h2ZM22,1H18a1,1,0,0,0-1,1V22a1,1,0,0,0,1,1h4a1,1,0,0,0,1-1V2A1,1,0,0,0,22,1ZM21,21H19V3h2ZM6,15H2a1,1,0,0,0-1,1v6a1,1,0,0,0,1,1H6a1,1,0,0,0,1-1V16A1,1,0,0,0,6,15ZM5,21H3V17H5Z" fill="currentColor"/>
        </svg>
      ),
      title: "Clean Code",
      description: "Built with robust, secure technology ensuring fast execution and reliable performance for all your trades."
    }
  ];

  const safetyFeatures = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-8 h-8">
          <path d="M19.63,3.65a1,1,0,0,0-.84-.2,8,8,0,0,1-6.22-1.27,1,1,0,0,0-1.14,0A8,8,0,0,1,5.21,3.45a1,1,0,0,0-.84.2A1,1,0,0,0,4,4.43v7.45a9,9,0,0,0,3.77,7.33l3.65,2.6a1,1,0,0,0,1.16,0l3.65-2.6A9,9,0,0,0,20,11.88V4.43A1,1,0,0,0,19.63,3.65ZM18,11.88a7,7,0,0,1-2.93,5.7L12,19.77,8.93,17.58A7,7,0,0,1,6,11.88V5.58a10,10,0,0,0,6-1.39,10,10,0,0,0,6,1.39ZM13.54,9.59l-2.69,2.7-.89-.9a1,1,0,0,0-1.42,1.42l1.6,1.6a1,1,0,0,0,1.42,0L15,11a1,1,0,0,0-1.42-1.42Z" fill="currentColor"/>
        </svg>
      ),
      title: "Bank-Grade Security",
      description: "Military-grade encryption and multi-layer security protocols to protect your assets and personal information."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-8 h-8">
          <path d="M10,20H5a1,1,0,0,1-1-1V5A1,1,0,0,1,5,4h5V7a3,3,0,0,0,3,3h4a1,1,0,0,0,.92-.62,1,1,0,0,0-.21-1.09l-6-6h0a1.07,1.07,0,0,0-.28-.19.32.32,0,0,0-.09,0L11.06,2H5A3,3,0,0,0,2,5V19a3,3,0,0,0,3,3h5a1,1,0,0,0,0-2ZM12,5.41,14.59,8H13a1,1,0,0,1-1-1ZM13,13a1,1,0,0,0-1-1H7a1,1,0,0,0,0,2h5A1,1,0,0,0,13,13ZM7,10H8A1,1,0,0,0,8,8H7a1,1,0,0,0,0,2Zm0,6a1,1,0,0,0,0,2H9a1,1,0,0,0,0-2Zm13-.82V15a3,3,0,0,0-6,0v.18A3,3,0,0,0,12,18v1a3,3,0,0,0,3,3h4a3,3,0,0,0,3-3V18A3,3,0,0,0,20,15.18ZM17,14a1,1,0,0,1,1,1H16A1,1,0,0,1,17,14Zm3,5a1,1,0,0,1-1,1H15a1,1,0,0,1-1-1V18a1,1,0,0,1,1-1h4a1,1,0,0,1,1,1Z" fill="currentColor"/>
        </svg>
      ),
      title: "Regulated License",
      description: "Fully licensed and compliant with international financial regulations and security standards."
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-8 h-8">
          <path d="M21.22,12A3,3,0,0,0,22,10a3,3,0,0,0-3-3H13.82A3,3,0,0,0,11,3H5A3,3,0,0,0,2,6a3,3,0,0,0,.78,2,3,3,0,0,0,0,4,3,3,0,0,0,0,4A3,3,0,0,0,2,18a3,3,0,0,0,3,3H19a3,3,0,0,0,2.22-5,3,3,0,0,0,0-4ZM11,19H5a1,1,0,0,1,0-2h6a1,1,0,0,1,0,2Zm0-4H5a1,1,0,0,1,0-2h6a1,1,0,0,1,0,2Zm0-4H5A1,1,0,0,1,5,9h6a1,1,0,0,1,0,2Zm0-4H5A1,1,0,0,1,5,5h6a1,1,0,0,1,0,2Zm8.69,11.71A.93.93,0,0,1,19,19H13.82a2.87,2.87,0,0,0,0-2H19a1,1,0,0,1,1,1A1,1,0,0,1,19.69,18.71Zm0-4A.93.93,0,0,1,19,15H13.82a2.87,2.87,0,0,0,0-2H19a1,1,0,0,1,1,1A1,1,0,0,1,19.69,14.71Zm0-4A.93.93,0,0,1,19,11H13.82a2.87,2.87,0,0,0,0-2H19a1,1,0,0,1,1,1A1,1,0,0,1,19.69,10.71Z" fill="currentColor"/>
        </svg>
      ),
      title: "Instant Results",
      description: "Lightning-fast trade execution with real-time results and comprehensive performance analytics."
    }
  ];

  const stats = [
    { value: "5.7M+", label: "Transactions" },
    { value: "70+", label: "Countries Served" },
    { value: "99.9%", label: "Uptime" },
    { value: "2.5M+", label: "Active Users" }
  ];

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'trading', label: 'Live Trading' },
    { id: 'features', label: 'Features' },
    { id: 'safety', label: 'Security' },
    { id: 'contacts', label: 'Contact' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-purple-900/20 text-white">
      {/* Enhanced Header */}
      <header className="fixed w-full bg-black/90 backdrop-blur-xl z-50 border-b border-gray-800/50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <button 
              onClick={() => handleNavigation('#home')}
              className="flex items-center space-x-3 focus:outline-none group"
            >
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-blue-500/25">
                <span className="font-bold text-white text-sm">WH</span>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Welthandel
              </span>
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1 bg-gray-900/50 rounded-2xl p-1 border border-gray-800/50">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavigation(`#${item.id}`)}
                  className={`px-4 py-2 rounded-xl transition-all duration-300 focus:outline-none text-sm font-medium ${
                    activeSection === item.id
                      ? 'bg-gradient-to-r from-blue-500/20 to-purple-600/20 text-white border border-blue-500/30 shadow-lg shadow-blue-500/10'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              
              {/* Google Translate - Improved Responsive */}
              <div className="relative ml-2">
                <div 
                  id="google_translate_element"
                  ref={translateRef}
                  className="min-w-[120px] max-w-[200px] scale-90"
                ></div>
                {!isTranslateLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-800/50 rounded-lg">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                  </div>
                )}
              </div>
            </nav>

            {/* Auth Buttons */}
            <div className="hidden md:flex items-center space-x-3">
              <button 
                onClick={() => handleNavigation('/login')}
                className="px-6 py-2 rounded-xl border border-gray-700 hover:border-gray-500 transition-all duration-300 focus:outline-none text-sm font-medium hover:bg-gray-800/50"
              >
                Sign In
              </button>
              <button 
                onClick={() => handleNavigation('/register')}
                className="bg-gradient-to-r from-blue-500 to-purple-600 px-6 py-2 rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 focus:outline-none shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 text-sm font-medium"
              >
                Get Started
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden flex flex-col space-y-1 focus:outline-none p-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`}></span>
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${isMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`w-6 h-0.5 bg-white transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}></span>
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-800/50 bg-black/95 backdrop-blur-xl">
              <nav className="flex flex-col space-y-3">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(`#${item.id}`)}
                    className={`px-4 py-3 rounded-xl text-left transition-all duration-300 focus:outline-none font-medium ${
                      activeSection === item.id
                        ? 'bg-gradient-to-r from-blue-500/20 to-purple-600/20 text-white border border-blue-500/30'
                        : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
                
                {/* Mobile Google Translate */}
                <div className="px-4 py-3">
                  <div 
                    // id="google_translate_element_mobile"
                     id="google_translate_element"
                    className="w-full"
                    ref={translateRef}
                  ></div>


                </div>
                
                <div className="flex space-x-3 px-4 pt-2">
                  <button 
                    onClick={() => handleNavigation('/login')}
                    className="flex-1 px-4 py-3 rounded-xl border border-gray-700 hover:border-gray-500 transition-all duration-300 focus:outline-none text-center font-medium hover:bg-gray-800/50"
                  >
                    Sign In
                  </button>
                  <button 
                    onClick={() => handleNavigation('/register')}
                    className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 px-4 py-3 rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 focus:outline-none text-center font-medium shadow-lg shadow-blue-500/25"
                  >
                    Get Started
                  </button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-purple-600/10"></div>
        <div className="absolute top-0 right-0 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl -translate-y-36 translate-x-36"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl translate-y-36 -translate-x-36"></div>
        
        <div className="container mx-auto px-4 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gray-800/50 border border-gray-700/50 text-sm text-gray-300 mb-6">
                🚀 Trusted by 2.5M+ traders worldwide
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-blue-200 to-purple-200 bg-clip-text text-transparent leading-tight">
                Welthandel
                <span className="block text-xl md:text-2xl lg:text-3xl text-blue-400 mt-4">
                  Next Generation Trading Platform
                </span>
              </h1>
              <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-lg mx-auto lg:mx-0 leading-relaxed">
                Institutional-grade security meets cutting-edge technology. Trade cryptocurrencies with confidence and precision.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button 
                  onClick={() => handleNavigation('/register')}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 px-8 py-4 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-300 focus:outline-none shadow-2xl shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-105 transform"
                >
                  Start Trading Now
                </button>
                <button 
                  onClick={() => handleNavigation('#trading')}
                  className="border border-gray-700 px-8 py-4 rounded-xl font-semibold hover:border-gray-500 transition-all duration-300 focus:outline-none hover:bg-gray-800/50 hover:scale-105 transform"
                >
                  View Live Charts
                </button>
              </div>
              
              {/* Trust Indicators */}
              <div className="flex flex-wrap gap-6 mt-12 justify-center lg:justify-start">
                {['🔒 Secure', '⚡ Fast', '💎 Reliable', '🌐 Global'].map((item, index) => (
                  <div key={index} className="flex items-center space-x-2 text-gray-400">
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Content - Trading Stats */}
            <div className="bg-gray-900/50 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-gray-800/50 shadow-2xl hover:border-gray-700/50 transition-all duration-300">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-2 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">Live Market Overview</h3>
                <p className="text-gray-400 mb-6">Real-time cryptocurrency prices</p>
                
                {/* Crypto Price Ticker */}
                <div className="space-y-4 mb-6">
                  {marketData.map((crypto, index) => (
                    <div key={index} className="flex justify-between items-center p-4 bg-gray-800/30 rounded-xl hover:bg-gray-800/50 transition-all duration-300 group cursor-pointer">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          crypto.trend === 'up' ? 'bg-green-500/10' : 'bg-red-500/10'
                        } group-hover:scale-110 transition-transform duration-300`}>
                          <span className="text-sm font-bold text-white">{crypto.symbol}</span>
                        </div>
                        <div>
                          <div className="font-semibold text-white">{crypto.symbol}</div>
                          <div className="text-xs text-gray-400">{crypto.name}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-white">{crypto.price}</div>
                        <div className={`text-sm font-medium ${
                          crypto.trend === 'up' ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {crypto.change}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <button 
                  onClick={() => handleNavigation('/register')}
                  className="w-full bg-gradient-to-r from-gray-800 to-gray-900 px-8 py-4 rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all duration-300 focus:outline-none border border-gray-700 hover:border-gray-600 font-semibold hover:scale-105 transform shadow-lg"
                >
                  CREATE FREE ACCOUNT
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Trading View Section */}
      <section id="trading" className="py-20 bg-gradient-to-b from-gray-900/50 to-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Advanced Trading Charts
            </h2>
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
              Professional trading tools with real-time market data, technical indicators, and advanced charting capabilities
            </p>
          </div>
          
          {/* TradingView Widget Container */}
          <div className="bg-gray-900/50 backdrop-blur-xl rounded-3xl p-4 md:p-6 border border-gray-800/50 shadow-2xl hover:border-gray-700/50 transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">BTC/USDT Chart</h3>
              <div className="flex space-x-2">
                <button className="px-3 py-1 text-xs bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors">1D</button>
                <button className="px-3 py-1 text-xs bg-blue-500/20 text-blue-400 rounded-lg border border-blue-500/30">1W</button>
                <button className="px-3 py-1 text-xs bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors">1M</button>
              </div>
            </div>
            
            <div 
              id="tradingview-widget"
              ref={tradingViewRef}
              className="h-[500px] md:h-[600px] rounded-2xl overflow-hidden bg-gray-900"
            >
              {/* TradingView widget will be loaded here */}
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
                <p className="text-lg">Loading advanced charts...</p>
                <p className="text-sm text-gray-600 mt-2">Powered by TradingView</p>
              </div>
            </div>
          </div>

          {/* Quick Stats Below Chart */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-8">
            {[
              { label: "24h Volume", value: "$2.4B", change: "+12.5%" },
              { label: "Active Traders", value: "45.2K", change: "+5.2%" },
              { label: "Pairs Available", value: "350+", change: "New" },
              { label: "Avg. Execution", value: "0.2s", change: "Fast" }
            ].map((stat, index) => (
              <div key={index} className="bg-gray-900/50 backdrop-blur-xl rounded-2xl p-4 md:p-6 text-center border border-gray-800/50 hover:border-gray-700/50 transition-all duration-300 group hover:transform hover:-translate-y-1">
                <div className="text-2xl md:text-3xl font-bold text-white mb-2 group-hover:scale-105 transition-transform duration-300">{stat.value}</div>
                <div className="text-gray-400 text-sm mb-1">{stat.label}</div>
                <div className="text-green-400 text-xs font-medium">{stat.change}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Why Choose Welthandel
            </h2>
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
              Experience the future of trading with our innovative platform designed for both beginners and professionals
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => (
              <div key={index} className="group bg-gray-900/50 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-gray-800/50 hover:border-blue-500/30 transition-all duration-500 hover:transform hover:-translate-y-2 shadow-xl hover:shadow-2xl">
                <div className="text-blue-400 mb-4 group-hover:scale-110 transition-transform duration-300 inline-flex p-3 bg-blue-500/10 rounded-2xl">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-4 text-white group-hover:text-blue-400 transition-colors duration-300">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed text-sm md:text-base">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section id="safety" className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Enterprise-Grade Security
            </h2>
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
              Your assets are protected with institutional-level security measures and advanced encryption
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {safetyFeatures.map((feature, index) => (
              <div key={index} className="text-center group">
                <div className="bg-gradient-to-br from-blue-500/10 to-purple-600/10 rounded-3xl p-6 md:p-8 inline-block mb-6 group-hover:scale-105 transition-transform duration-300 border border-blue-500/20 hover:border-blue-500/40 shadow-lg">
                  <div className="text-blue-400">
                    {feature.icon}
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-4 text-white group-hover:text-blue-400 transition-colors duration-300">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed text-sm md:text-base">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-purple-900/20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="text-3xl md:text-4xl lg:text-5xl font-bold mb-2 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent group-hover:scale-110 transition-transform duration-300">
                  {stat.value}
                </div>
                <div className="text-gray-400 uppercase text-xs md:text-sm tracking-wider font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-500/10 via-purple-600/10 to-blue-500/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
            Ready to Start Trading?
          </h2>
          <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
            Join millions of traders worldwide and experience the future of digital asset trading with institutional-grade tools and security.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => handleNavigation('/register')}
              className="bg-gradient-to-r from-blue-500 to-purple-600 px-8 md:px-12 py-4 rounded-xl text-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-300 focus:outline-none shadow-2xl shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-105 transform"
            >
              Create Free Account
            </button>
            <button 
              onClick={() => handleNavigation('/login')}
              className="border border-gray-700 px-8 md:px-12 py-4 rounded-xl text-lg font-semibold hover:border-gray-500 transition-all duration-300 focus:outline-none hover:bg-gray-800/50 hover:scale-105 transform"
            >
              Sign In to Dashboard
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacts" className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Get in Touch
            </h2>
            <p className="text-lg text-gray-300 leading-relaxed max-w-2xl mx-auto">
              Our dedicated support team is available 24/7 to assist you with any questions about our platform, security features, or trading tools.
            </p>
          </div>
          <div className="max-w-md mx-auto">
            <div className="bg-gray-900/50 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-gray-800/50 shadow-2xl hover:border-gray-700/50 transition-all duration-300">
              <ul className="space-y-4">
                <li className="flex items-center space-x-4 p-4 bg-gray-800/30 rounded-xl hover:bg-gray-800/50 transition-colors duration-300 group">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6 text-blue-400 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <path d="M19,4H5A3,3,0,0,0,2,7V17a3,3,0,0,0,3,3H19a3,3,0,0,0,3-3V7A3,3,0,0,0,19,4Zm-.41,2-5.88,5.88a1,1,0,0,1-1.42,0L5.41,6ZM20,17a1,1,0,0,1-1,1H5a1,1,0,0,1-1-1V7.41l5.88,5.88a3,3,0,0,0,4.24,0L20,7.41Z" fill="currentColor"/>
                  </svg>
                  <a href="https://t.me/Heidi0047" className="text-gray-300 hover:text-white transition-colors duration-300 font-medium">
                    Telegram
                  </a>
                </li>
                <li className="flex items-center space-x-4 p-4 bg-gray-800/30 rounded-xl hover:bg-gray-800/50 transition-colors duration-300 group">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-6 h-6 text-blue-400 flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <path d="M12,2A10,10,0,0,0,2,12c0,4.42,2.87,8.17,6.84,9.5.5.08.66-.23.66-.5V19.31c-2.34.49-2.84-1.13-2.84-1.13-.46-1.16-1.11-1.47-1.11-1.47-.9-.62.07-.6.07-.6,1,.07,1.53,1,1.53,1,.87,1.52,2.34,1.07,2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92,0-1.11.38-2,1-2.71-.1-.25-.45-1.29.1-2.64,0,0,.84-.27,2.75,1,.79-.22,1.65-.33,2.5-.33s1.71.11,2.5.33c1.91-1.27,2.75-1,2.75-1,.55,1.35.2,2.39.1,2.64.62.71,1,1.6,1,2.71,0,3.82-2.34,4.66-4.57,4.91.36.31.69.92.69,1.85V21c0,.27.16.59.67.5C19.14,20.16,22,16.42,22,12A10,10,0,0,0,12,2Z" fill="currentColor"/>
                  </svg>
                  <span className="text-gray-300 font-medium">24/7 Live Support</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900/80 backdrop-blur-xl py-12 border-t border-gray-800/50">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex justify-center items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/25">
                <span className="font-bold text-white text-xs">WH</span>
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
               Welthandel INVESTMENT LIMITED
              </span>
            </div>
            <p className="text-gray-400 text-sm">
              &copy; 2025 Welthandel INVESTMENT LIMITED. All rights reserved. | Secure • Fast • Reliable
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;